import type { CalendarEntry, CalendarGroup, CalendarId, LinkedCalendarEntry, LinkedCalendarEntryV2, LocalCacheForRemoteCalendarEntry, LocalCalendarEntry, } from "owa-graph-schema";
import getCalendarGroupKey from "../utils/getCalendarGroupKey";
import getStore from "../store/store";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isShadowMailboxUser } from "owa-session-store";
import { getCalculatedFolderId, isLinkedCalendarEntry, isLinkedCalendarEntryV2, } from "owa-calendar-properties";
import { findInArray } from "owa-calendar-data-utils/lib/findInArray";
import { isCalendarInAccount } from "../utils/compareCalendarUtils";
import { getAccountKeyForMailboxInfo, isSameCoprincipalAccountByMailboxAndAccountKey, } from "owa-client-types";
import type { AccountKey, MailboxInfo } from "owa-client-types";
import { isBootFeatureEnabled } from "owa-metatags";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWritableCalendar from "../utils/isWritableCalendar";
import { areCalendarEntriesOrganizationDuplicates, areCalendarEntriesInSameAccount, areMailboxInfosInSameAccount, } from "../utils/calendarDuplicateUtils";
import type CalendarGroupType from "owa-service/lib/contract/CalendarGroupType";
import { getCalendarIdForFolderId } from "../utils/calendarFolderIdMappingUtils";

export function getDefaultCalendar(accountKey?: AccountKey): CalendarEntry | null | undefined {
    return getStore().defaultCalendars.get(accountKey ? accountKey : getAccountKeyForMailboxInfo(getGlobalSettingsAccountMailboxInfo()));
}

export function isDefaultCalendar(calendarId: CalendarId, accountKey?: AccountKey): boolean {
    const defaultCalendar = getDefaultCalendar(accountKey);
    return !!defaultCalendar && calendarId.id === defaultCalendar.calendarId.id;
}

export function getCalendarEntries(): CalendarEntry[] {
    const { calendarEntryMapping, calendarIdOrderedList } = getStore();
    
    // always return ordered list
    const calendarEntries: CalendarEntry[] = [];
    for (const calendarId of calendarIdOrderedList) {
        const calendarEntry = calendarEntryMapping.get(calendarId);
        if (calendarEntry) {
            calendarEntries.push(calendarEntry);
        }
    }
    
    return calendarEntries;
}

export function getCalendarIndex(calendarId: string): number {
    const calendarIdFindResult = findInArray(getStore().calendarIdOrderedList, id => id === calendarId);
    if (calendarIdFindResult) {
        return calendarIdFindResult.index;
    }
    else {
        return -1;
    }
}

export function getCalendarIdOrderedList(): string[] {
    return getStore().calendarIdOrderedList;
}

export function getCalendarGroups(): CalendarGroup[] {
    const { calendarGroupsMapping, calendarGroupKeyOrderedList } = getStore();
    
    const calendarGroups: CalendarGroup[] = [];
    // always return ordered list
    for (const calendarGroupKey of calendarGroupKeyOrderedList) {
        const calendarGroup = calendarGroupsMapping.get(calendarGroupKey);
        if (calendarGroup) {
            calendarGroups.push(calendarGroup);
        }
    }
    
    return calendarGroups;
}

export function getCalendarGroupIndex(calendarGroupId: string, mailboxInfo: MailboxInfo): number {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const groupKey = getCalendarGroupKey(accountKey, calendarGroupId);
    const calendarGroupKeyFindResult = findInArray(getStore().calendarGroupKeyOrderedList, key => key === groupKey);
    if (calendarGroupKeyFindResult) {
        return calendarGroupKeyFindResult.index;
    }
    else {
        return -1;
    }
}

export function getCalendarGroupKeyOrderedList(): string[] {
    return getStore().calendarGroupKeyOrderedList;
}

export function getCalendarGroupByGroupId(calendarGroupId: string, mailboxInfo: MailboxInfo): CalendarGroup | undefined {
    const { calendarGroupsMapping } = getStore();
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const groupKey = getCalendarGroupKey(accountKey, calendarGroupId);
    return calendarGroupsMapping.get(groupKey);
}

/**
 * **Important:** Even when the calendar cache is initialized, it is not a guarantee that the folder id associated with an
 * event will be present in the cache, because some calendar folder ids need to be updated after initialization. This is true
 * of all cache functions that use calendar folder id to select data.
 * See `getAndUpdateActualFolderId` for details.
 *
 * @param folderId the calendar folder id
 */
export function getCalendarIdByFolderId(folderId: string | null | undefined, mailboxInfo?: MailboxInfo): CalendarId | undefined {
    if (folderId) {
        return getCalendarIdForFolderId(getStore(), folderId, mailboxInfo);
    }
    
    return undefined;
}

export function getCalendarEntryByCalendarId(calendarId: string): CalendarEntry | undefined {
    const { calendarEntryMapping } = getStore();
    return calendarEntryMapping.get(calendarId);
}

export function getCalendarIdBySharedFolderId(sharedFolderId: string): CalendarId | undefined {
    if (sharedFolderId) {
        const { sharedFolderIdMapping } = getStore();
        return sharedFolderIdMapping.get(sharedFolderId);
    }
    
    return undefined;
}

export function getCalendarEntryByFolderId(folderId: string | null | undefined, mailboxInfo?: MailboxInfo): CalendarEntry | undefined {
    if (folderId) {
        const calendarId = getCalendarIdByFolderId(folderId, mailboxInfo);
        // If the calendar ID is present then get the CalendarEntry for it otherwise return undefined
        return calendarId ? getCalendarEntryByCalendarId(calendarId.id) : undefined;
    }
    
    return undefined;
}

export function getCalendarFolderId(calendar: CalendarEntry): string {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (131,5): Type 'string | null' is not assignable to type 'string'.
    // @ts-expect-error
    return getFolderIdByCalendarID(calendar.calendarId.id);
}

export function getFolderIdByCalendarID(calendarId: string): string | null {
    const { calendarEntryMapping } = getStore();
    const calendarEntry = calendarEntryMapping.get(calendarId);
    
    if (calendarEntry) {
        const folderId = getCalculatedFolderId(calendarEntry);
        return folderId ? folderId.Id : null;
    }
    
    return null;
}

export function getCalculatedFolderIdByCalendarID(calendarId: string): string | null {
    const { calendarEntryMapping } = getStore();
    
    const calendarEntry = calendarEntryMapping.get(calendarId);
    if (calendarEntry) {
        const folderId = getCalculatedFolderId(calendarEntry);
        return folderId ? folderId.Id : null;
    }
    return null;
}

export function getCalculatedFolderIdForDefaultCalendar(mailboxInfo?: MailboxInfo): string | null {
    const accountKey = mailboxInfo ? getAccountKeyForMailboxInfo(mailboxInfo) : undefined;
    const defaultCalendar = getDefaultCalendar(accountKey);
    
    if (defaultCalendar) {
        const folderId = getCalculatedFolderId(defaultCalendar);
        return folderId ? folderId.Id : null;
    }
    
    return null;
}

export function calendarIsInCache(calendarId: string): boolean {
    return getCalendarEntryByCalendarId(calendarId) ? true : false;
}

/**
 * Returns whether this folder is a LocalCalendarEntry or a LocalCacheForRemoteCalendarEntry
 * that has a valid CalendarFolderId
 * @param folderId FolderId of the calendar folder
 */
export function isLocalCalendarEntry(folderId: string | null | undefined, mailboxInfo?: MailboxInfo): boolean {
    if (!folderId) {
        return false;
    }
    const { calendarEntryMapping } = getStore();
    
    const calendarId = getCalendarIdByFolderId(folderId, mailboxInfo);
    if (calendarId) {
        const calendarEntry: any = calendarEntryMapping.get(calendarId.id);
        
        // Group mailboxes are Linked calendar entries
        return (calendarEntry &&
            !calendarEntry.IsGroupMailboxCalendar &&
            !isLinkedCalendarEntry(calendarEntry) &&
            !isLinkedCalendarEntryV2(calendarEntry));
    }
    else {
        return false;
    }
}

/**
 * Returns whether the given folder is a readonly local calendar entry
 * @param folderId The folder id
 * @returns True if it's a read only local calendar entry, false otherwise
 */
export function isReadOnlyLocalCalendar(folderId: string): boolean {
    if (isLocalCalendarEntry(folderId)) {
        const calendarEntry: LocalCalendarEntry = getCalendarEntryByFolderId(folderId) as LocalCalendarEntry;
        return !!calendarEntry?.IsReadOnly;
    }
    return false;
}

/**
 * Checks whether the calendarEntry has been marked as an invalid folder
 * @param calendarEntry the calendar folder to check for validity
 */
export function isValidCalendarEntry(calendarEntry?: CalendarEntry): boolean {
    const { validEntryMapping } = getStore();
    if (calendarEntry &&
        isBootFeatureEnabled("cal-enable-HybridModel-for-calendarSharing") &&
        isLinkedCalendarEntryV2(calendarEntry)) {
        return true;
    }
    
    return !!calendarEntry && !!validEntryMapping.get(calendarEntry.calendarId.id);
}

/**
 * Checks if the calendar folder id needs to be updated.
 * @returns true if the calendar is an old-model shared calendar i.e. `LinkedCalendarEntry` calendar that has not been
 * updated via `getAndUpdateActualFolderId`, otherwise returns false.
 *
 * When the cache is initialized LinkedCalendarEntry
 * calendar entries do not have accurate folder ids. These ids can be fetched/ updated
 * using `getAndUpdateActualFolderId`. Once the folder Id is updated for the calendar, this
 * calendar entry is marked as valid, and `calendarFolderIdNeedsUpdate` will return false.
 */
export function calendarFolderIdNeedsUpdate(calendarId: string): boolean {
    const calendarEntry = getCalendarEntryByCalendarId(calendarId);
    const folderId = getFolderIdByCalendarID(calendarId);
    if (!isLocalCalendarEntry(folderId, calendarEntry?.calendarId.mailboxInfo)) {
        return !isValidCalendarEntry(calendarEntry);
    }
    return false;
}

export function getCalendarEntryByEmailAddress(emailAddress?: string, mailboxInfo?: MailboxInfo): CalendarEntry | null | undefined {
    if (!emailAddress) {
        return null;
    }
    
    const { calendarEntryMapping } = getStore();
    const calendarId = getCalendarIdByFolderId(emailAddress, mailboxInfo);
    let calendarEntry;
    
    // First check if we have a direct email -> calendarId mapping (common for shared/delegate calendars)
    // If not found, search through all calendar entries by OwnerEmailAddress
    if (calendarId) {
        calendarEntry = calendarEntryMapping.get(calendarId.id);
    }
    else {
        const entries = [...calendarEntryMapping.values()] as (LinkedCalendarEntryV2 | LocalCacheForRemoteCalendarEntry | LinkedCalendarEntry)[];
        
        const matchingEntries = entries.filter(entry => {
            return (entry.OwnerEmailAddress &&
                entry.OwnerEmailAddress.toLowerCase() === emailAddress.toLowerCase() &&
                (!mailboxInfo ||
                    areMailboxInfosInSameAccount(entry.calendarId.mailboxInfo, mailboxInfo)));
        });
        
        // When multiple calendars match (e.g., delegate scenarios with LinkedCalendarEntry + LocalCacheForRemoteCalendarEntry),
        // prefer entries that have actual folder IDs to load events from.
        // Priority: Both SharedFolderId AND FolderId (LocalCacheForRemoteCalendarEntry for delegates) > FolderId only > SharedFolderId only > any match
        if (matchingEntries.length > 1) {
            // First try to find entry with BOTH SharedFolderId and FolderId (most complete - delegate's local cache)
            const entryWithBothIds = matchingEntries.find(e => e.SharedFolderId?.Id && e.FolderId?.Id);
            if (entryWithBothIds) {
                calendarEntry = entryWithBothIds;
            }
            else {
                // Fall back to entry with FolderId only (resolvable by getCalendarEntryByFolderId)
                const entryWithFolder = matchingEntries.find(e => e.FolderId?.Id);
                if (entryWithFolder) {
                    calendarEntry = entryWithFolder;
                }
                else {
                    // Fall back to entry with SharedFolderId only
                    const entryWithSharedFolder = matchingEntries.find(e => e.SharedFolderId?.Id);
                    calendarEntry = entryWithSharedFolder || matchingEntries[0];
                }
            }
        }
        else {
            calendarEntry = matchingEntries[0];
        }
    }
    
    return calendarEntry;
}

export function getCalendarGroupsForUser(accountKey: AccountKey): {
    userCalendarGroups: CalendarGroup[];
    groupCalendarGroups: CalendarGroup[];
    teamsCalendarGroups: CalendarGroup[];
    teamRhythmCalendarGroups: CalendarGroup[];
} {
    const calendarGroups = getCalendarGroups();
    const userCalendarGroups = calendarGroups.filter(calendarGroup => ["UserMailbox", "PstFile"].includes(calendarGroup.calendarGroupId.mailboxInfo.type) &&
        isSameCoprincipalAccountByMailboxAndAccountKey(calendarGroup.calendarGroupId.mailboxInfo, accountKey));
    const groupCalendarGroups = calendarGroups.filter(calendarGroup => calendarGroup.calendarGroupId.mailboxInfo.type === "GroupMailbox" &&
        isSameCoprincipalAccountByMailboxAndAccountKey(calendarGroup.calendarGroupId.mailboxInfo, accountKey));
    
    const teamsCalendarGroups = calendarGroups.filter(calendarGroup => calendarGroup.calendarGroupId.mailboxInfo.type === "TeamsMailbox" &&
        isSameCoprincipalAccountByMailboxAndAccountKey(calendarGroup.calendarGroupId.mailboxInfo, accountKey));
    
    const teamRhythmCalendarGroups = calendarGroups.filter(calendarGroup => calendarGroup.calendarGroupId.mailboxInfo.type === "TeamRhythm" &&
        isSameCoprincipalAccountByMailboxAndAccountKey(calendarGroup.calendarGroupId.mailboxInfo, accountKey));
    
    return {
        userCalendarGroups,
        groupCalendarGroups,
        teamsCalendarGroups,
        teamRhythmCalendarGroups,
    };
}

export function getCalendarsForUser(mailboxInfo: MailboxInfo): CalendarEntry[] {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    
    return getCalendarEntries().filter(calendarEntry => isCalendarInAccount(calendarEntry, accountKey));
}

export function getCalendarsForCalendarGroup(mailboxInfo: MailboxInfo, parentGroupId: string | undefined): CalendarEntry[] {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    
    const calendars = getCalendarEntries().filter(calendarEntry => {
        return (calendarEntry &&
            calendarEntry.ParentGroupId === parentGroupId &&
            isSameCoprincipalAccountByMailboxAndAccountKey(calendarEntry.calendarId.mailboxInfo, accountKey));
    });
    
    return calendars;
}

export function getShareableCalendars(): CalendarEntry[] {
    const result: CalendarEntry[] = [];
    getCalendarEntries().forEach(calendar => {
        if (calendar.CanShare) {
            result.push(calendar);
        }
    });
    return result;
}

export function getWritableCalendars(): CalendarEntry[] {
    const result: CalendarEntry[] = [];
    getCalendarEntries().forEach(calendar => {
        if (isWritableCalendar(calendar)) {
            result.push(calendar);
        }
    });
    return result;
}

// Returns all shareable calendars and writable cloud cache calendars
export function getWritableCloudCacheCalendars(): CalendarEntry[] {
    const result: CalendarEntry[] = [];
    getCalendarEntries().forEach(calendar => {
        const isCloudCacheCalendar = isShadowMailboxUser(calendar.calendarId.mailboxInfo);
        if (isWritableCalendar(calendar) && isCloudCacheCalendar) {
            result.push(calendar);
        }
        else if (calendar.CanShare) {
            result.push(calendar);
        }
    });
    return result;
}

// Returns selectable calendars for ICS import based on feature flags.
// Centralises the filtering logic shared by ImportFromFilePanel and IcsPreviewFields.
export function getSelectableCalendarsForImport(): CalendarEntry[] {
    if (isGlobalFeatureEnabled("cal-ics-import-editableCalendars")) {
        return getWritableCalendars();
    }
    else if (isGlobalFeatureEnabled("cal-ics-import-cloudCacheCalendars")) {
        return getWritableCloudCacheCalendars();
    }
    return getShareableCalendars();
}

export function getCalendarMailboxInfoFromFolderId(folderId: string | null | undefined): MailboxInfo {
    const calendarId = getCalendarIdByFolderId(folderId);
    return calendarId?.mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
}

export function getDuplicateCalendarId(calendarId: string): CalendarId | null {
    return getStore().calendarDuplicateIdMapping.get(calendarId) ?? null;
}

export function getDuplicateCalendarEntry(calendarId: string, mailboxInfo?: MailboxInfo): CalendarEntry | undefined {
    const { calendarDuplicateIdMapping } = getStore();
    const calendarEntry = getCalendarEntryByCalendarId(calendarId);
    const duplicateCalendarId = calendarDuplicateIdMapping.get(calendarId);
    const duplicateCalendarEntry = duplicateCalendarId
        ? getCalendarEntryByCalendarId(duplicateCalendarId.id)
        : undefined;
    if (calendarEntry &&
        duplicateCalendarEntry &&
        (!mailboxInfo ||
            areMailboxInfosInSameAccount(duplicateCalendarEntry.calendarId.mailboxInfo, mailboxInfo)) &&
        areCalendarEntriesInSameAccount(calendarEntry, duplicateCalendarEntry) &&
        areCalendarEntriesOrganizationDuplicates(calendarEntry, duplicateCalendarEntry, getCalendarGroupByGroupId(calendarEntry.ParentGroupId, calendarEntry.calendarId.mailboxInfo)?.GroupType === 4, getCalendarGroupByGroupId(duplicateCalendarEntry.ParentGroupId, duplicateCalendarEntry.calendarId.mailboxInfo)?.GroupType === 4) &&
        ((!isLinkedCalendarEntry(duplicateCalendarEntry) &&
            !isLinkedCalendarEntryV2(duplicateCalendarEntry)) ||
            isValidCalendarEntry(duplicateCalendarEntry))) {
        return duplicateCalendarEntry;
    }
    return undefined;
}
